import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	ListItemIcon,
	Menu,
	MenuItem,
	Skeleton,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import React, {
	ChangeEvent,
	FC,
	FormEvent,
	MouseEvent,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LoadingButton } from "@mui/lab";
import {
	DataGrid,
	GridColDef,
	GridColumnVisibilityModel,
	GridPagination,
	GridPaginationModel,
	GridRenderCellParams,
	GridValueFormatterParams,
	GridValueGetterParams
} from "@mui/x-data-grid";
import moment from "moment";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import BlockIcon from "@mui/icons-material/Block";
import GridViewIcon from "@mui/icons-material/GridView";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import FilterListIcon from "@mui/icons-material/FilterList";

import "./dashboard.css";
import { ReactComponent as CarIcon } from "../../assets/icons/car-icon.svg";
import { ReactComponent as BikeIcon } from "../../assets/icons/bike-icon.svg";
import { ReactComponent as CarEntryIcon } from "../../assets/icons/car-entry.svg";
import { ReactComponent as CarExitIcon } from "../../assets/icons/car-exit.svg";
import { ReactComponent as BikeEntryIcon } from "../../assets/icons/bike-entry.svg";
import { ReactComponent as BikeExitIcon } from "../../assets/icons/bike-exit.svg";
import { ReactComponent as UnauthorizedIcon } from "../../assets/icons/unauthorized.svg";
import { ReactComponent as VisitorIcon } from "../../assets/icons/visitor.svg";
import { ReactComponent as DashCarEntryIcon } from "../../assets/icons/dash-car-entry.svg";
import { ReactComponent as DashCarExitIcon } from "../../assets/icons/dash-car-exit.svg";
import { ReactComponent as DashBikeEntryIcon } from "../../assets/icons/dash-bike-entry.svg";
import { ReactComponent as DashBikeExitIcon } from "../../assets/icons/dash-bike-exit.svg";
import AddVisitorDialog from "./AddVisitorDialog";
import { usePageLayoutContext } from "../../router/PageLayout";
import {
	useGetEventsListQuery,
	useGetLineChartDataQuery,
	useGetUptimeChartDataQuery,
	useGetUptimeStatsQuery,
	useGetUserSettingsQuery,
	useGetVehicleAuthChartDataQuery,
	useGetVehicleMetricsQuery,
	useGetVehicleTypesChartDataQuery
} from "../../redux/reducers/dashboard.reducer";
import {
	IDisplayNotification,
	IGetDashboardDetailsRequestData,
	IGetEventsListRequestData,
	IGetVehicleAuthChartResponseData,
	INotificationListItem,
	IVehicleBrandDetails,
	IVehicleCategoryDetails,
	IVehicleColorDetails
} from "../../interfaces";
import {
	CONFIG_MODE_E,
	DASHBOARD_CHART_REFRESH_INTERVAL,
	DASHBOARD_EVENTS_REFRESH_INTERVAL,
	EVENT_LOGS_REQUEST_TIME_FORMAT,
	ROWS_PER_PAGE,
	RPI_DEVICE_NAME,
	VEHICLE_TYPE_E,
	VEHICLE_TYPES_LIST
} from "../../utils/constants";
import LogDetailsDialog from "./LogDetailsDialog";
import { useAppDispatch, useAppSelector } from "../../redux";
import { removeNotification } from "../../redux/reducers/notifications.reducer";
import downloadServices from "../../redux/services/download.services";
import TablePagination from "../../common/TablePagination";
import { useGetAllBlacklistedVehiclesQuery } from "../../redux/reducers/dataManagement.reducers";
import {
	useGetCamerasListQuery,
	useGetSiteConfigQuery,
	useGetSocietyConfigQuery,
	useGetVehicleBrandsQuery,
	useGetVehicleCategoryQuery,
	useGetVehicleColorsQuery
} from "../../redux/reducers/settings.reducer";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import VehicleCountChart from "./VehicleCountChart";
import UptimeChart from "./UptimeChart";
import VehicleMetricsCards from "./VehicleMetricsCards";
import TableSettingsDialog from "./TableSettingsDialog";
import EventLogImage from "../../common/EventLogImage";

type LogFilterType = "authorized" | "blacklisted" | "unauthorized" | "visitor";

export interface IVehicleEventDetails {
	id: number;
	vehicle_type: number;
	timestamp: string;
	name: string;
	vehicle_number: string;
	plate_image: string;
	location: string;
	site_id: number;
	is_entry: boolean;
	type: LogFilterType;
	unique_id: string;
	flat_number: string;
	contact_number: string;
	purpose_of_visit: string;
	comment: string;
	address: string;
	custom_field_data: { [key: string]: string };
	vehicle_brand: IVehicleBrandDetails | null;
	vehicle_category: IVehicleCategoryDetails | null;
	vehicle_color: IVehicleColorDetails | null;
	bbox: [number, number, number, number][];
	hashed_number_plate: string;
}

export interface ILineChartData {
	timestamps: string[];
	entry: number[];
	exit: number[];
}

interface IDateUptimeChart {
	start_time: string;
	end_time: string;
	is_online: boolean;
	duration: number;
}

export interface IUptimeChartData {
	[data: string]: IDateUptimeChart[];
}

export interface IRpiLineChartData {
	timestamps: string[];
	registered: number[];
	unregistered: number[];
	visitor: number[];
	bikes: number[];
	cars: number[];
}

export type ColumnVisibilityDetailsType = {
	checked: boolean;
	visible: boolean;
	disabled: boolean;
};

export type ColumnVisibilityType = {
	[columnName: string]: ColumnVisibilityDetailsType;
};

const DEFAULT_CHECKED_COLUMNS = ["icon", "date", "time", "vehicle_number"];

const DashboardPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ spacer: "grid-pagination-spacer-custom" }}
	/>
);

const Dashboard: FC = () => {
	const { timeFilter, timeFilterType } = usePageLayoutContext();

	const dispatch = useAppDispatch();

	const notificationsState = useAppSelector((state) => state.notifications);
	const authState = useAppSelector((state) => state.auth);

	const componentRenderCheck = useRef<boolean>(false);

	const locationDropdownAnchorRef = useRef<HTMLDivElement>(null);

	const vehicleBrandDropdownAnchorRef = useRef<HTMLDivElement>(null);
	const vehicleCategoryDropdownAnchorRef = useRef<HTMLDivElement>(null);
	const vehicleColorDropdownAnchorRef = useRef<HTMLDivElement>(null);
	const vehicleTypeDropdownAnchorRef = useRef<HTMLDivElement>(null);

	const alertPageChanging = useRef<"next" | "previous" | null>(null);

	const [selectedGraphType, setSelectedGraphType] = useState<"vehicle_count" | "uptime_count">("vehicle_count");
	const [selectedLogsFilter, setSelectedLogsFilter] = useState<LogFilterType | null>(null);
	const [searchInput, setSearchInput] = useState<string>("");
	const [showAddVisitorDialog, setShowAddVisitorDialog] = useState<boolean>(false);
	const [lineChartData, setLineChartData] = useState<ILineChartData>({
		timestamps: [],
		entry: [],
		exit: []
	});
	const [eventsListFilters, setEventListFilters] = useState<IGetEventsListRequestData>({
		start_date: timeFilter.startTime,
		end_date: timeFilter.endTime,
		page: 1
	});
	const [showLogDetailsDialog, setShowLogDetailsDialog] = useState<boolean>(false);
	const [selectedLogDetails, setSelectedLogDetails] = useState<IVehicleEventDetails | null>(null);
	const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
	const [addVisitorSelectedEvent, setAddVisitorSelectedEvent] = useState<IVehicleEventDetails | null>(null);
	const [showDownloadButtonLoader, setShowDownloadButtonLoader] = useState<boolean>(false);

	const [displayNotifications, setDisplayNotifications] = useState<IDisplayNotification[]>([]);

	const [logsFilterDropdownAnchorElement, setLogsFilterDropdownAnchorElement] = useState<HTMLElement | null>(null);
	const logsFilterDropdownOpen = Boolean(logsFilterDropdownAnchorElement);

	const [directionDropdownAnchorElement, setDirectionDropdownAnchorElement] = useState<HTMLDivElement | null>(null);
	const directionDropdownOpen = Boolean(directionDropdownAnchorElement);

	const [locationDropdownOpen, setLocationDropdownOpen] = useState<boolean>(false);
	const [vehicleBrandDropdownOpen, setVehicleBrandDropdownOpen] = useState<boolean>(false);
	const [vehicleCategoryDropdownOpen, setVehicleCategoryDropdownOpen] = useState<boolean>(false);
	const [vehicleColorDropdownOpen, setVehicleColorDropdownOpen] = useState<boolean>(false);
	const [vehicleTypeDropdownOpen, setVehicleTypeDropdownOpen] = useState<boolean>(false);
	const [showTableSettingsDialog, setShowTableSettingsDialog] = useState<boolean>(false);
	const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityType>({});
	const [finalColumnVisibility, setFinalColumnVisibility] = useState<ColumnVisibilityType>({});

	const ColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
		const model: GridColumnVisibilityModel = {};

		for (const [columnName, visibilityDetails] of Object.entries(finalColumnVisibility)) {
			model[columnName] = visibilityDetails.checked && visibilityDetails.visible;
		}

		return model;
	}, [finalColumnVisibility]);

	// APIS
	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const { data: getCamerasListResponse } = useGetCamerasListQuery({});
	const camerasList = useMemo(() => getCamerasListResponse?.results ?? [], [getCamerasListResponse]);

	const { data: allBlacklistedVehicles } = useGetAllBlacklistedVehiclesQuery(null, { pollingInterval: 60 * 1000 });

	const { data: getVehicleBrandsResponse } = useGetVehicleBrandsQuery();
	const vehicleBrandsList = useMemo(() => getVehicleBrandsResponse ?? [], [getVehicleBrandsResponse]);

	const { data: getVehicleCategoriesResponse } = useGetVehicleCategoryQuery();
	const vehicleCategoriesList = useMemo(() => getVehicleCategoriesResponse ?? [], [getVehicleCategoriesResponse]);

	const { data: getVehicleColorsResponse } = useGetVehicleColorsQuery();
	const vehicleColorsList = useMemo(() => getVehicleColorsResponse ?? [], [getVehicleColorsResponse]);

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyConfig =
		getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
			? getSocietyConfigResponse.results[0]
			: null;

	const lineChartFilter = useMemo<IGetDashboardDetailsRequestData>(() => {
		const filterData: IGetDashboardDetailsRequestData = {
			start_date: timeFilter.startTime,
			end_date: timeFilter.endTime
		};

		const endTime = moment(timeFilter.endTime, EVENT_LOGS_REQUEST_TIME_FORMAT);

		if (endTime.isSame(new Date(), "day") && endTime.isAfter(moment())) {
			filterData.end_date = moment().format(EVENT_LOGS_REQUEST_TIME_FORMAT);
		}

		return filterData;
	}, [timeFilter]);

	const {
		data: getLineChartDataResponse,
		isLoading: getLineChartDataLoading,
		isFetching: getLineChartDataFetching
	} = useGetLineChartDataQuery(lineChartFilter, {
		pollingInterval: isFilterApplied ? 0 : DASHBOARD_CHART_REFRESH_INTERVAL * 1000,
		skip: selectedGraphType !== "vehicle_count"
	});

	const {
		data: getVehicleMetricsResponse,
		isLoading: getVehicleMetricsLoading,
		isFetching: getVehicleMetricsFetching
	} = useGetVehicleMetricsQuery(
		{ start_date: timeFilter.startTime, end_date: timeFilter.endTime },
		{ pollingInterval: isFilterApplied ? 0 : DASHBOARD_EVENTS_REFRESH_INTERVAL * 1000 }
	);

	const {
		data: getEventsListResponse,
		isLoading: getEventsListLoading,
		isFetching: getEventsListFetching,
		refetch: refetchEventsList
	} = useGetEventsListQuery(eventsListFilters, {
		pollingInterval: isFilterApplied ? 0 : DASHBOARD_EVENTS_REFRESH_INTERVAL * 1000
	});

	const {
		data: uptimeStatsData,
		isLoading: getUptimeStatsLoading,
		isFetching: getUptimeStatsFetching
	} = useGetUptimeStatsQuery(
		{ start_date: timeFilter.startTime, end_date: timeFilter.endTime },
		{ pollingInterval: isFilterApplied ? 0 : DASHBOARD_EVENTS_REFRESH_INTERVAL * 1000 }
	);

	const { data: getUptimeChartDataResponse } = useGetUptimeChartDataQuery(
		{ start_date: timeFilter.startTime, end_date: timeFilter.endTime },
		{
			pollingInterval: isFilterApplied ? 0 : DASHBOARD_CHART_REFRESH_INTERVAL * 1000,
			skip: selectedGraphType !== "uptime_count"
		}
	);

	const {
		data: getVehicleAuthChartResponse,
		isLoading: getVehicleAuthChartLoading,
		isFetching: getVehicleAuthChartFetching
	} = useGetVehicleAuthChartDataQuery(lineChartFilter);

	const {
		data: getVehicleTypesChartResponse,
		isLoading: getVehicleTypesChartLoading,
		isFetching: getVehicleTypesChartFetching
	} = useGetVehicleTypesChartDataQuery(lineChartFilter);

	const {
		data: getUserSettingsResponse,
		isLoading: getUserSettingsLoading,
		isFetching: getUserSettingsFetching,
		refetch: refetchUserSettings
	} = useGetUserSettingsQuery(null);

	const rowsData = useMemo(() => {
		if (!getEventsListResponse) return [];

		const rows: IVehicleEventDetails[] = getEventsListResponse.results.map((eventItem) => ({
			id: eventItem.id,
			vehicle_type: eventItem.vehicle_type,
			timestamp: moment(eventItem.timestamp).format(EVENT_LOGS_REQUEST_TIME_FORMAT),
			name: eventItem.name,
			vehicle_number: eventItem.number_plate,
			plate_image: eventItem.plate_image_url,
			location: eventItem.camera_name,
			site_id: eventItem.camera,
			is_entry: eventItem.is_entry,
			type: eventItem.is_blacklisted
				? "blacklisted"
				: eventItem.visitor
				? "visitor"
				: eventItem.auth
				? "authorized"
				: "unauthorized",
			unique_id: eventItem.unique_id,
			flat_number: eventItem.flat_number,
			address: eventItem.visitor?.address ?? "",
			comment: eventItem.visitor?.purpose || "-",
			contact_number: eventItem.visitor?.phone_number ?? "",
			purpose_of_visit: eventItem.visitor?.visit_type?.type_of ?? "",
			custom_field_data: eventItem.visitor?.custom_field_data ?? {},
			vehicle_brand: vehicleBrandsList.find((item) => item.id === eventItem.vehicle_brand) ?? null,
			vehicle_category: vehicleCategoriesList.find((item) => item.id === eventItem.vehicle_category) ?? null,
			vehicle_color: vehicleColorsList.find((item) => item.id === eventItem.color) ?? null,
			bbox: eventItem.bbox ?? [],
			hashed_number_plate: eventItem.number_plate_hash ?? ""
		}));

		if (alertPageChanging.current === "previous") {
			const alertDetails = rows[rows.length - 1];

			handleOpenLogDetailsDialog(alertDetails);

			alertPageChanging.current = null;
		} else if (alertPageChanging.current === "next") {
			const alertDetails = rows[0];

			handleOpenLogDetailsDialog(alertDetails);

			alertPageChanging.current = null;
		}

		return rows;
	}, [getEventsListResponse, vehicleBrandsList, vehicleCategoriesList, vehicleColorsList]);

	const detailsPreviousButtonDisabled = useMemo<boolean>(() => {
		if (selectedLogDetails && typeof eventsListFilters.page === "number" && eventsListFilters.page === 1) {
			const selectedAlertIndex = rowsData.findIndex(
				(alertItem) => alertItem.unique_id === selectedLogDetails.unique_id
			);

			if (selectedAlertIndex === 0) {
				return true;
			}
		}

		return false;
	}, [eventsListFilters.page, rowsData, selectedLogDetails]);

	const detailsNextButtonDisabled = useMemo<boolean>(() => {
		if (selectedLogDetails) {
			const totalCount = getEventsListResponse?.count ?? 0;
			const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE);

			if (typeof eventsListFilters.page === "number" && eventsListFilters.page === totalPages) {
				const selectedAlertIndex = rowsData.findIndex(
					(alertItem) => alertItem.unique_id === selectedLogDetails.unique_id
				);

				if (selectedAlertIndex === (totalCount % ROWS_PER_PAGE) - 1) {
					return true;
				}
			}
		}

		return false;
	}, [eventsListFilters.page, getEventsListResponse, rowsData, selectedLogDetails]);

	const UptimeChartData = useMemo<IUptimeChartData>(() => {
		let updatedUptimeChartData: IUptimeChartData = {};

		if (getUptimeChartDataResponse && getUptimeChartDataResponse.length > 0) {
			for (const uptimeDataItem of getUptimeChartDataResponse) {
				const date = moment(uptimeDataItem.start_time).format("YYYY-MM-DD");

				if (!updatedUptimeChartData[date]) {
					updatedUptimeChartData[date] = [];
				}

				updatedUptimeChartData[date].push({
					start_time: uptimeDataItem.start_time,
					end_time: uptimeDataItem.end_time,
					is_online: uptimeDataItem.is_online > 0,
					duration: uptimeDataItem.duration_minutes
				});
			}
		}

		return updatedUptimeChartData;
	}, [getUptimeChartDataResponse]);

	function convertTo24HourFormat(time: string): string {
		const [hour, period] = time.split(" ");

		if (hour === "12" && period === "AM") return "00:00";
		if (period === "AM") return `${hour.padStart(2, "0")}:00`;
		if (period === "PM" && hour === "12") return "12:00";

		return `${parseInt(hour, 10) + 12}:00`;
	}

	const convertRangeToSortableValue = useCallback((range: string): string => {
		const [start, end] = range.split(" - ");

		const startTime = convertTo24HourFormat(start);
		const endTime = convertTo24HourFormat(end);

		return `${startTime}-${endTime}`;
	}, []);

	function parseDate(date: string): Date {
		const [day, month, year] = date.split(" ");

		const monthIndex = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].indexOf(
			month
		);

		return new Date(parseInt(year), monthIndex, parseInt(day));
	}

	const getAllTimestamps = useCallback(
		(data: IGetVehicleAuthChartResponseData): string[] => {
			const allTimestamps: string[] = [];

			for (const timestamp of Object.keys(data)) {
				if (!allTimestamps.includes(timestamp)) allTimestamps.push(timestamp);
			}

			if (allTimestamps[0] && (allTimestamps[0].includes("AM") || allTimestamps[0].includes("PM"))) {
				return allTimestamps.sort((a, b) => {
					const convertedA = convertRangeToSortableValue(a);
					const convertedB = convertRangeToSortableValue(b);

					if (convertedA < convertedB) return -1;
					if (convertedA > convertedB) return 1;
					return 0;
				});
			}

			return allTimestamps.sort((a, b) => {
				const dateA = parseDate(a);
				const dateB = parseDate(b);
				return dateA.getTime() - dateB.getTime();
			});
		},
		[convertRangeToSortableValue]
	);

	const VehicleChartData = useMemo<IRpiLineChartData>(() => {
		const data: IRpiLineChartData = {
			timestamps: [],
			registered: [],
			unregistered: [],
			visitor: [],
			bikes: [],
			cars: []
		};

		if (getVehicleAuthChartResponse && getVehicleTypesChartResponse) {
			data.timestamps = getAllTimestamps(getVehicleAuthChartResponse);

			for (const timestamp of data.timestamps) {
				data.registered.push(getVehicleAuthChartResponse[timestamp]?.registered_count ?? null);
				data.unregistered.push(getVehicleAuthChartResponse[timestamp]?.unregistered_count ?? null);
				data.visitor.push(getVehicleAuthChartResponse[timestamp]?.visitor_count ?? null);
				data.bikes.push(getVehicleTypesChartResponse[timestamp]?.["Two Wheeler"] ?? null);
				data.cars.push(getVehicleTypesChartResponse[timestamp]?.["Four Wheeler"] ?? null);
			}
		}

		return data;
	}, [getAllTimestamps, getVehicleAuthChartResponse, getVehicleTypesChartResponse]);

	function handleChangeSelectedGraphType(updatedType: "vehicle_count" | "uptime_count") {
		setSelectedGraphType(updatedType);
	}

	function handleOpenFilterDropdown(event: MouseEvent<HTMLButtonElement>) {
		setLogsFilterDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseFilterDropdown() {
		setLogsFilterDropdownAnchorElement(null);
	}

	function handleOpenAddVisitorDialog(eventDetails: IVehicleEventDetails | null) {
		setAddVisitorSelectedEvent(eventDetails);
		setShowAddVisitorDialog(true);
	}

	function handleCloseAddVisitorDialog() {
		setShowAddVisitorDialog(false);
		setAddVisitorSelectedEvent(null);
		refetchEventsList();
	}

	function handleOpenLogDetailsDialog(logDetails: IVehicleEventDetails) {
		setSelectedLogDetails(logDetails);
		setShowLogDetailsDialog(true);
	}

	function handleCloseLogDetailsDialog() {
		setShowLogDetailsDialog(false);
		setSelectedLogDetails(null);
	}

	function handleChangeSelectedLogsFilter(updatedValue: LogFilterType | null) {
		handleCloseFilterDropdown();
		setSelectedLogsFilter(updatedValue);

		setIsFilterApplied(true);

		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			switch (updatedValue) {
				case "authorized":
					updatedFilters.is_auth = true;
					delete updatedFilters.blacklisted;
					delete updatedFilters.visitor;
					break;

				case "blacklisted":
					updatedFilters.blacklisted = true;
					delete updatedFilters.is_auth;
					delete updatedFilters.visitor;
					break;

				case "unauthorized":
					updatedFilters.is_auth = false;
					delete updatedFilters.blacklisted;
					updatedFilters.visitor = false;
					break;

				case "visitor":
					updatedFilters.visitor = true;
					delete updatedFilters.blacklisted;
					delete updatedFilters.is_auth;
					break;

				default:
					delete updatedFilters.blacklisted;
					delete updatedFilters.is_auth;
					delete updatedFilters.visitor;
					break;
			}

			return updatedFilters;
		});
	}

	function handleChangeSearchInput(event: ChangeEvent<HTMLInputElement>) {
		setSearchInput(event.target.value);
	}

	function handleVehicleLogsPageChange(event: GridPaginationModel) {
		setEventListFilters((currentFiltersState) => ({
			...currentFiltersState,
			page: event.page + 1
		}));
		setIsFilterApplied(true);
	}

	function handleSubmitSearchForm(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setIsFilterApplied(true);

		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput.trim();
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleClearSearch() {
		setSearchInput("");
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };
			delete updatedFilters.search;
			return updatedFilters;
		});
	}

	function handleRemoveCurrentNotification(messageId: string) {
		dispatch(removeNotification(messageId));
	}

	function handleDownloadButtonClick() {
		setShowDownloadButtonLoader(true);
		downloadServices
			.handleDownloadEventLogs(eventsListFilters)
			.catch((error) => {
				console.error("Download event logs failed ::", error);
			})
			.finally(() => {
				setShowDownloadButtonLoader(false);
			});
	}

	function handleOpenTableSettingsDialog() {
		setShowTableSettingsDialog(true);
	}

	function handleCloseTableSettingsDialog(success?: boolean) {
		setShowTableSettingsDialog(false);
		refetchUserSettings();
		if (success) {
			setFinalColumnVisibility(columnVisibility);
		} else {
			setColumnVisibility(finalColumnVisibility);
		}
	}

	function handleOpenDirectionDropdown(event: MouseEvent<HTMLDivElement>) {
		setDirectionDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseDirectionDropdown() {
		setDirectionDropdownAnchorElement(null);
	}

	function handleChangeSelectedDirection(updatedValue: "entry" | "exit" | null) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue) updatedFilters.is_entry = updatedValue === "entry";
			else delete updatedFilters.is_entry;

			return updatedFilters;
		});

		handleCloseDirectionDropdown();
		setIsFilterApplied(true);
	}

	function handleOpenLocationDropdown() {
		setLocationDropdownOpen(true);
	}

	function handleCloseLocationDropdown() {
		setLocationDropdownOpen(false);
	}

	function handleChangeSelectedLocation(updatedValue: number) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.camera_id = updatedValue;
			else delete updatedFilters.camera_id;

			return updatedFilters;
		});

		handleCloseLocationDropdown();
	}

	function handleOpenVehicleBrandDropdown() {
		setVehicleBrandDropdownOpen(true);
	}

	function handleCloseVehicleBrandDropdown() {
		setVehicleBrandDropdownOpen(false);
	}

	function handleChangeSelectedVehicleBrand(updatedValue: number) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.vehicle_brand = updatedValue;
			else delete updatedFilters.vehicle_brand;

			return updatedFilters;
		});

		handleCloseVehicleBrandDropdown();
	}

	function handleOpenVehicleCategoryDropdown() {
		setVehicleCategoryDropdownOpen(true);
	}

	function handleCloseVehicleCategoryDropdown() {
		setVehicleCategoryDropdownOpen(false);
	}

	function handleChangeSelectedVehicleCategory(updatedValue: number) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.vehicle_category = updatedValue;
			else delete updatedFilters.vehicle_category;

			return updatedFilters;
		});

		handleCloseVehicleCategoryDropdown();
	}

	function handleOpenVehicleColorDropdown() {
		setVehicleColorDropdownOpen(true);
	}

	function handleCloseVehicleColorDropdown() {
		setVehicleColorDropdownOpen(false);
	}

	function handleChangeSelectedVehicleColor(updatedValue: number) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.color = updatedValue;
			else delete updatedFilters.color;

			return updatedFilters;
		});

		handleCloseVehicleColorDropdown();
	}

	function handleOpenVehicleTypeDropdown() {
		setVehicleTypeDropdownOpen(true);
	}

	function handleCloseVehicleTypeDropdown() {
		setVehicleTypeDropdownOpen(false);
	}

	function handleChangeSelectedVehicleType(updatedValue: number) {
		setEventListFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (updatedValue >= 0) updatedFilters.vehicle_type = updatedValue;
			else delete updatedFilters.vehicle_type;

			return updatedFilters;
		});

		handleCloseVehicleTypeDropdown();
	}

	function handleChangeToPreviousAlert(eventId: string) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.unique_id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === 0) {
			if (eventsListFilters.page && eventsListFilters.page > 1) {
				alertPageChanging.current = "previous";
				setEventListFilters((currentFilters) => {
					const updatedFilters = { ...currentFilters };
					if (typeof updatedFilters.page === "number") {
						updatedFilters.page = updatedFilters.page - 1;
					}
					return updatedFilters;
				});
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex - 1];

		handleOpenLogDetailsDialog(alertDetails);
	}

	function handleChangeToNextAlert(eventId: string) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.unique_id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === rowsData.length - 1) {
			const totalCount = getEventsListResponse?.count ?? 0;
			const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE);

			if (eventsListFilters.page && eventsListFilters.page < totalPages) {
				alertPageChanging.current = "next";
				setEventListFilters((currentFilters) => {
					const updatedFilters = { ...currentFilters };
					if (typeof updatedFilters.page === "number") {
						updatedFilters.page = updatedFilters.page + 1;
					}
					return updatedFilters;
				});
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex + 1];

		handleOpenLogDetailsDialog(alertDetails);
	}

	const showNotification = useCallback(
		(notificationsList: INotificationListItem[]) => {
			const updatedDisplayNotifications: IDisplayNotification[] = [];

			for (const notificationItem of notificationsList) {
				const displayNotificationItem: IDisplayNotification = {
					...notificationItem,
					data: {
						...notificationItem.data,
						auth_type: notificationItem.data.is_auth ? "authorized" : "unauthorized"
					}
				};

				if (
					displayNotificationItem.data.auth_type === "unauthorized" &&
					allBlacklistedVehicles &&
					allBlacklistedVehicles.length > 0
				) {
					const blacklistVehicle = allBlacklistedVehicles.find(
						(vehiclePlate) => vehiclePlate === notificationItem.data.number_plate
					);

					if (blacklistVehicle) {
						displayNotificationItem.data.auth_type = "blacklisted";
					}
				}

				updatedDisplayNotifications.push(displayNotificationItem);
			}

			setDisplayNotifications(updatedDisplayNotifications);
		},
		[allBlacklistedVehicles]
	);

	useEffect(() => {
		componentRenderCheck.current = true;

		return () => {
			componentRenderCheck.current = false;
		};
	}, []);

	useEffect(() => {
		setEventListFilters((currentFilters) => ({
			...currentFilters,
			page: 1,
			start_date: timeFilter.startTime,
			end_date: timeFilter.endTime
		}));

		if (timeFilterType !== "today") {
			setIsFilterApplied(true);
		}
	}, [timeFilter, timeFilterType]);

	useEffect(() => {
		if (getLineChartDataResponse) {
			const updatedTimestampsData: string[] = [];
			const updatedEntryData: number[] = [];
			const updatedExitData: number[] = [];

			for (const [timestamp, lineChartDataItem] of Object.entries(getLineChartDataResponse)) {
				updatedTimestampsData.push(timestamp);
				updatedEntryData.push(lineChartDataItem.entry);
				updatedExitData.push(lineChartDataItem.exit);
			}

			setLineChartData({
				timestamps: updatedTimestampsData,
				entry: updatedEntryData,
				exit: updatedExitData
			});
		}
	}, [getLineChartDataResponse]);

	useEffect(() => {
		showNotification(notificationsState.notifications);
	}, [notificationsState.notifications, showNotification]);

	useEffect(() => {
		let columns: ColumnVisibilityType = {};

		if (
			getUserSettingsResponse &&
			getUserSettingsResponse.user_settings &&
			getUserSettingsResponse.user_settings.columns
		) {
			for (const columnDetails of columnsData) {
				const columnName = columnDetails.field;

				if (columnName === "info") continue;

				const columnVisibilityDetails: ColumnVisibilityDetailsType = {
					checked: getUserSettingsResponse.user_settings.columns[columnName] ?? true,
					disabled: false,
					visible: true
				};

				if (columnName === "vehicle_brand") {
					columnVisibilityDetails.visible = vehicleBrandsList.length > 0;
				}

				if (columnName === "vehicle_category") {
					columnVisibilityDetails.visible = vehicleCategoriesList.length > 0;
				}

				if (columnName === "vehicle_color") {
					columnVisibilityDetails.visible = vehicleColorsList.length > 0;
				}

				if (columnName === "is_entry" || columnName === "location") {
					columnVisibilityDetails.visible = !!siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME;
				}

				if (columnName === "hashed_number_plate") {
					columnVisibilityDetails.visible = !!siteConfig && siteConfig.mode === CONFIG_MODE_E.MY_GATE;
				}

				if (DEFAULT_CHECKED_COLUMNS.includes(columnName)) {
					columnVisibilityDetails.checked = true;
					columnVisibilityDetails.disabled = true;
				}

				columns[columnName] = columnVisibilityDetails;
			}

			if (societyConfig && societyConfig.custom_fields && societyConfig.custom_fields.length > 0) {
				for (const customFieldItem of societyConfig.custom_fields) {
					if (customFieldItem.name === "Purpose of Visit") continue;

					if (DEFAULT_CHECKED_COLUMNS.includes(customFieldItem.name)) {
						columns[customFieldItem.name] = {
							checked: true,
							disabled: true,
							visible: true
						};
					} else {
						columns[customFieldItem.name] = {
							checked: getUserSettingsResponse.user_settings.columns[customFieldItem.name] ?? true,
							disabled: false,
							visible: true
						};
					}
				}
			}
		}

		setColumnVisibility(columns);
		setFinalColumnVisibility(columns);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getUserSettingsResponse, siteConfig, societyConfig, vehicleBrandsList, vehicleCategoriesList, vehicleColorsList]);

	function renderSelectedFilterIcon(logFilterKey: LogFilterType | null) {
		switch (logFilterKey) {
			case "authorized":
				return <PlaylistAddCheckIcon color="inherit" fontSize="inherit" />;

			case "blacklisted":
				return <BlockIcon color="inherit" fontSize="inherit" />;

			case "unauthorized":
				return <UnauthorizedIcon fill="inherit" className="vehicle-type-icon" />;

			case "visitor":
				return <VisitorIcon fill="inherit" className="vehicle-type-icon" />;

			default:
				return <GridViewIcon color="inherit" fontSize="inherit" />;
		}
	}

	function renderDirectionDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="direction-dropdown"
					aria-controls={directionDropdownOpen ? "direction-dropdown-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={directionDropdownOpen ? "true" : undefined}
					onClick={handleOpenDirectionDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					<Typography textTransform="capitalize" fontSize={16} fontWeight={500}>
						{"is_entry" in eventsListFilters ? (eventsListFilters.is_entry ? "Entry" : "Exit") : "Direction"}
					</Typography>
					{directionDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="direction-dropdown-menu"
					anchorEl={directionDropdownAnchorElement}
					open={directionDropdownOpen}
					onClose={handleCloseDirectionDropdown}
					MenuListProps={{ "aria-labelledby": "direction-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedDirection(null)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					<MenuItem onClick={() => handleChangeSelectedDirection("entry")}>Entry</MenuItem>
					<MenuItem onClick={() => handleChangeSelectedDirection("exit")}>Exit</MenuItem>
				</Menu>
			</Box>
		);
	}

	function renderLocationDropdown() {
		return (
			<Box>
				<Box
					ref={locationDropdownAnchorRef}
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="location-dropdown"
					aria-controls="location-dropdown-menu"
					aria-haspopup="true"
					aria-expanded={locationDropdownOpen}
					onClick={handleOpenLocationDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					<Typography textTransform="capitalize" fontSize={16} fontWeight={500}>
						{typeof eventsListFilters.camera_id === "number"
							? camerasList.find((item) => item.id === eventsListFilters.camera_id)?.camera_site ?? "Location"
							: "Location"}
					</Typography>

					{locationDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="location-dropdown-menu"
					anchorEl={locationDropdownAnchorRef.current}
					open={locationDropdownOpen}
					onClose={handleCloseLocationDropdown}
					MenuListProps={{ "aria-labelledby": "location-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedLocation(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{camerasList.map((cameraItem) => (
						<MenuItem key={cameraItem.id} onClick={() => handleChangeSelectedLocation(cameraItem.camera_id)}>
							{cameraItem.camera_site}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	function renderVehicleBrandDropdown() {
		return (
			<Box>
				<Box
					ref={vehicleBrandDropdownAnchorRef}
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="vehicle-brand-dropdown"
					aria-controls="vehicle-brand-dropdown-menu"
					aria-haspopup
					aria-expanded={vehicleBrandDropdownOpen}
					onClick={handleOpenVehicleBrandDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					<Typography textTransform="capitalize" fontSize={16} fontWeight={500}>
						{typeof eventsListFilters.vehicle_brand === "number"
							? vehicleBrandsList.find((item) => item.id === eventsListFilters.vehicle_brand)?.name ?? "Brand"
							: "Brand"}
					</Typography>

					{vehicleBrandDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="vehicle-brand-dropdown-menu"
					anchorEl={vehicleBrandDropdownAnchorRef.current}
					open={vehicleBrandDropdownOpen}
					onClose={handleCloseVehicleBrandDropdown}
					MenuListProps={{ "aria-labelledby": "vehicle-brand-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedVehicleBrand(-1)} sx={{ minWidth: 140 }}>
						<em>All</em>
					</MenuItem>

					{vehicleBrandsList.map((vehicleBrandItem) => (
						<MenuItem key={vehicleBrandItem.id} onClick={() => handleChangeSelectedVehicleBrand(vehicleBrandItem.id)}>
							{vehicleBrandItem.name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	function renderVehicleCategoryDropdown() {
		return (
			<Box>
				<Box
					ref={vehicleCategoryDropdownAnchorRef}
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="vehicle-category-dropdown"
					aria-controls="vehicle-category-dropdown-menu"
					aria-haspopup
					aria-expanded={vehicleCategoryDropdownOpen}
					onClick={handleOpenVehicleCategoryDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					<Typography textTransform="capitalize" fontSize={16} fontWeight={500}>
						{typeof eventsListFilters.vehicle_category === "number"
							? vehicleCategoriesList.find((item) => item.id === eventsListFilters.vehicle_category)?.name ?? "Type"
							: "Type"}
					</Typography>

					{vehicleCategoryDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="vehicle-category-dropdown-menu"
					anchorEl={vehicleCategoryDropdownAnchorRef.current}
					open={vehicleCategoryDropdownOpen}
					onClose={handleCloseVehicleCategoryDropdown}
					MenuListProps={{ "aria-labelledby": "vehicle-category-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedVehicleCategory(-1)} sx={{ minWidth: 155 }}>
						<em>All</em>
					</MenuItem>

					{vehicleCategoriesList.map((vehicleCategoryItem) => (
						<MenuItem
							key={vehicleCategoryItem.id}
							onClick={() => handleChangeSelectedVehicleCategory(vehicleCategoryItem.id)}
						>
							{vehicleCategoryItem.name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	function renderVehicleColorDropdown() {
		return (
			<Box>
				<Box
					ref={vehicleColorDropdownAnchorRef}
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="vehicle-color-dropdown"
					aria-controls="vehicle-color-dropdown-menu"
					aria-haspopup
					aria-expanded={vehicleColorDropdownOpen}
					onClick={handleOpenVehicleColorDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					<Typography textTransform="capitalize" fontSize={16} fontWeight={500}>
						{typeof eventsListFilters.color === "number"
							? vehicleColorsList.find((item) => item.id === eventsListFilters.color)?.name ?? "Color"
							: "Color"}
					</Typography>

					{vehicleColorDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="vehicle-color-dropdown-menu"
					anchorEl={vehicleColorDropdownAnchorRef.current}
					open={vehicleColorDropdownOpen}
					onClose={handleCloseVehicleColorDropdown}
					MenuListProps={{ "aria-labelledby": "vehicle-color-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedVehicleColor(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{vehicleColorsList.map((vehicleColorItem) => (
						<MenuItem key={vehicleColorItem.id} onClick={() => handleChangeSelectedVehicleColor(vehicleColorItem.id)}>
							{vehicleColorItem.name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	function renderVehicleTypeDropdown() {
		return (
			<Box>
				<Box
					ref={vehicleTypeDropdownAnchorRef}
					component="div"
					className="table-column-header-dropdown-wrapper"
					id="vehicle-type-dropdown"
					aria-controls="vehicle-type-dropdown-menu"
					aria-haspopup
					aria-expanded={vehicleTypeDropdownOpen}
					onClick={handleOpenVehicleTypeDropdown}
					sx={{ alignItems: "center", justifyContent: "center" }}
				>
					{/* <Typography fontSize={16} fontWeight={500}>
						{typeof eventsListFilters.vehicle_type === "number"
							? VEHICLE_TYPES_LIST.find((vehicleTypeItem) => vehicleTypeItem.id === eventsListFilters.vehicle_type)
									?.name ?? "Type"
							: "Type"}
					</Typography> */}

					{typeof eventsListFilters.vehicle_type === "number" ? (
						<>
							{eventsListFilters.vehicle_type === VEHICLE_TYPE_E.FOUR_WHEELER ? (
								<CarIcon width={24} fill="var(--color-primary-main)" />
							) : (
								<BikeIcon width={24} fill="var(--color-primary-main)" />
							)}

							{vehicleTypeDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
						</>
					) : (
						<FilterListIcon fontSize="small" />
					)}
				</Box>

				<Menu
					id="vehicle-type-dropdown-menu"
					anchorEl={vehicleTypeDropdownAnchorRef.current}
					open={vehicleTypeDropdownOpen}
					onClose={handleCloseVehicleTypeDropdown}
					MenuListProps={{ "aria-labelledby": "vehicle-type-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedVehicleType(-1)} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{VEHICLE_TYPES_LIST.map((vehicleTypeItem) => (
						<MenuItem key={vehicleTypeItem.id} onClick={() => handleChangeSelectedVehicleType(vehicleTypeItem.id)}>
							<ListItemIcon>
								{vehicleTypeItem.id === VEHICLE_TYPE_E.FOUR_WHEELER ? (
									<CarIcon width={22} fill="var(--color-primary-main)" />
								) : (
									<BikeIcon width={22} fill="var(--color-primary-main)" />
								)}
							</ListItemIcon>

							{vehicleTypeItem.name}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "icon",
			headerName: "Type",
			width: 75,
			sortable: false,
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			renderHeader: renderVehicleTypeDropdown,
			renderCell: (params: GridRenderCellParams<IVehicleEventDetails>) => (
				<Box className="event-vehicle-type-icon">
					<Box className={`event-type-identifier-color ${params.row.type}`} />

					{params.row.is_entry ? (
						params.row.vehicle_type === 0 ? (
							<DashCarEntryIcon width={20} />
						) : (
							<DashBikeEntryIcon width={20} />
						)
					) : params.row.vehicle_type === 0 ? (
						<DashCarExitIcon width={20} />
					) : (
						<DashBikeExitIcon width={20} />
					)}
				</Box>
			)
		},
		{
			field: "date",
			headerName: "Date",
			flex: 1,
			minWidth: 110,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			valueGetter: (params: GridValueGetterParams<IVehicleEventDetails>) =>
				moment(params.row.timestamp, EVENT_LOGS_REQUEST_TIME_FORMAT).format("DD/MM/YYYY")
		},
		{
			field: "time",
			headerName: "Time",
			flex: 1,
			minWidth: 100,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			valueGetter: (params: GridValueGetterParams<IVehicleEventDetails>) =>
				moment(params.row.timestamp, EVENT_LOGS_REQUEST_TIME_FORMAT).format("hh:mm A")
		},
		{
			field: "name",
			headerName: "Name",
			flex: 1,
			minWidth: 150,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`
		},
		{
			field: "vehicle_number",
			headerName: "Registration Number",
			flex: 1.5,
			minWidth: 180,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`
		},
		{
			field: "hashed_number_plate",
			headerName: "Number Plate Hash",
			flex: 1.5,
			minWidth: 300,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`
		},
		{
			field: "plate_image",
			headerName: "Plate Image",
			width: 220,
			sortable: false,
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			renderCell: (params: GridRenderCellParams<IVehicleEventDetails>) => <EventLogImage src={params.value ?? ""} />
		},
		{
			field: "is_entry",
			headerName: "Direction",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			valueFormatter: (params: GridValueFormatterParams<boolean>) => (params.value ? "Entry" : "Exit"),
			renderHeader: renderDirectionDropdown
		},
		{
			field: "location",
			headerName: "Location",
			flex: 1,
			minWidth: 150,
			sortable: false,
			align: "center",
			// headerAlign: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			renderHeader: renderLocationDropdown
		},
		{
			field: "vehicle_brand",
			headerName: "Brand",
			flex: 1,
			minWidth: 100,
			sortable: false,
			align: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			renderHeader: renderVehicleBrandDropdown,
			renderCell: (params: GridRenderCellParams<IVehicleEventDetails, IVehicleBrandDetails | null>) =>
				params.value ? (
					<Tooltip
						title={convertSnakeToTitleCase(params.value.name)}
						placement="bottom"
						slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -14] } }] } }}
					>
						<Box className="dashboard-vehicle-logs-vehicle-brand-cell-wrapper">
							<img src={params.value.icon} alt={params.value.name} />
						</Box>
					</Tooltip>
				) : undefined
		},
		{
			field: "vehicle_category",
			headerName: "Type",
			flex: 1,
			minWidth: 175,
			sortable: false,
			align: "center",
			headerClassName: `vehicle-logs-table-column-headers show-separator ${selectedLogsFilter}`,
			renderHeader: renderVehicleCategoryDropdown,
			valueFormatter: (params: GridValueFormatterParams<IVehicleCategoryDetails | null>) =>
				params.value ? params.value.name.replace(/_/g, "") : ""
		},
		{
			field: "vehicle_color",
			headerName: "Color",
			flex: 1,
			minWidth: 100,
			sortable: false,
			align: "center",
			headerClassName: `vehicle-logs-table-column-headers ${selectedLogsFilter} ${
				societyConfig && societyConfig.custom_fields && societyConfig.custom_fields.length > 0 ? "show-separator" : ""
			}`,
			renderHeader: renderVehicleColorDropdown,
			renderCell: (params: GridRenderCellParams<IVehicleEventDetails, IVehicleColorDetails | null>) =>
				params.value ? (
					<Box className="dashboard-vehicle-logs-vehicle-color-cell-wrapper">
						<Typography variant="body2">{convertSnakeToTitleCase(params.value.name)}</Typography>

						<Box className="vehicle-color-wrapper">
							<img src={params.value.icon} alt={params.value.name} />
						</Box>
					</Box>
				) : undefined
		}
	];

	function getColumnsData() {
		const updatedColumnsData = [...columnsData];

		if (societyConfig && societyConfig.custom_fields && societyConfig.custom_fields.length > 0) {
			for (const [index, customFieldItem] of Object.entries(societyConfig.custom_fields)) {
				if (customFieldItem.name === "Purpose of Visit") continue;

				updatedColumnsData.push({
					field: customFieldItem.name,
					headerName: customFieldItem.name,
					flex: 1,
					minWidth: 130,
					sortable: false,
					align: "center",
					headerAlign: "center",
					headerClassName: `vehicle-logs-table-column-headers ${
						Number(index) < societyConfig.custom_fields.length - 1 ? "show-separator" : ""
					} ${selectedLogsFilter}`,
					valueGetter: (params: GridValueGetterParams<IVehicleEventDetails>) =>
						params.row.custom_field_data[customFieldItem.name]
				});
			}
		}

		updatedColumnsData.push({
			field: "info",
			headerName: "",
			width: 75,
			sortable: false,
			headerClassName: `vehicle-logs-table-column-headers ${selectedLogsFilter}`,
			renderCell: (params: GridRenderCellParams<IVehicleEventDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
					{siteConfig && siteConfig.mode !== CONFIG_MODE_E.MY_GATE && params.row.type === "unauthorized" ? (
						<IconButton size="small" onClick={() => handleOpenAddVisitorDialog(params.row)}>
							<AddCircleIcon fontSize="inherit" color="success" />
						</IconButton>
					) : null}

					<IconButton size="small" onClick={() => handleOpenLogDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" color="info" />
					</IconButton>
				</Box>
			)
		});

		return updatedColumnsData;
	}

	const RenderGraph: { [key in "vehicle_count" | "uptime_count"]: ReactNode } = {
		vehicle_count: (
			<VehicleCountChart
				data={lineChartData}
				loading={
					siteConfig && siteConfig.device_type && siteConfig.device_type === RPI_DEVICE_NAME
						? getVehicleAuthChartLoading ||
						  getVehicleAuthChartFetching ||
						  getVehicleTypesChartLoading ||
						  getVehicleTypesChartFetching
						: getLineChartDataLoading || getLineChartDataFetching
				}
				rpiData={
					siteConfig && siteConfig.device_type && siteConfig.device_type === RPI_DEVICE_NAME
						? VehicleChartData
						: undefined
				}
			/>
		),
		uptime_count: <UptimeChart data={UptimeChartData} />
	};

	return (
		<Box className="dashboard-screen-wrapper">
			<Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} columns={10}>
				{/* ROW 1 */}
				<Grid item xs={10} lg={7}>
					{selectedGraphType === "vehicle_count" &&
					lineChartData.entry.length <= 0 &&
					(getLineChartDataLoading || getLineChartDataFetching) ? (
						<Skeleton variant="rounded" animation="wave" height="447px" />
					) : (
						<Box className="dashboard-content-section">
							<Box className="graph-section-header">
								<Typography
									variant="h6"
									fontWeight={500}
									color="var(--color-text-primary)"
									sx={{ fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.375rem" } }}
								>
									{selectedGraphType === "vehicle_count" ? "Vehicle Count" : "Uptime"}
								</Typography>

								<Box className="graph-selector">
									<Box
										className={`graph-selector-tab left ${selectedGraphType === "vehicle_count" ? "active" : ""}`}
										onClick={() => handleChangeSelectedGraphType("vehicle_count")}
									>
										<Typography variant="body2">Vehicle Count</Typography>
									</Box>

									<Box
										className={`graph-selector-tab right ${selectedGraphType === "uptime_count" ? "active" : ""}`}
										onClick={() => handleChangeSelectedGraphType("uptime_count")}
									>
										<Typography variant="body2">Uptime</Typography>
									</Box>
								</Box>
							</Box>

							{RenderGraph[selectedGraphType]}
						</Box>
					)}
				</Grid>

				<Grid item xs={10} lg={3}>
					<VehicleMetricsCards
						vehicleStats={getVehicleMetricsResponse}
						uptimeStats={uptimeStatsData}
						loading={
							(!getVehicleMetricsResponse && (getVehicleMetricsLoading || getVehicleMetricsFetching)) ||
							(!uptimeStatsData && (getUptimeStatsLoading || getUptimeStatsFetching))
						}
						isRpiDevice={siteConfig && siteConfig.device_type === RPI_DEVICE_NAME}
					/>
				</Grid>

				{/* ROW 2 */}
				{siteConfig && siteConfig.device_type && siteConfig.device_type !== RPI_DEVICE_NAME ? (
					<Grid item xs={10} md={5}>
						{!getVehicleMetricsResponse && (getVehicleMetricsLoading || getVehicleMetricsFetching) ? (
							<Skeleton variant="rounded" animation="wave" height="153px" />
						) : (
							<Box className="dashboard-content-section dashboard-vehicle-counts-section-wrapper">
								<Typography
									variant="h6"
									color="var(--color-primary-main)"
									sx={{ fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.375rem" } }}
								>
									Entry Details
								</Typography>

								<Divider sx={{ borderColor: "#00000061" }} />

								<Box className="dashboard-vehicle-counts-content-wrapper">
									<Box className="dashboard-vehicle-counts-content authorized">
										<Typography
											fontWeight={600}
											color="var(--color-success-main)"
											sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Authorized
										</Typography>

										<Box className="dashboard-vehicle-counts">
											<Box className="dashboard-vehicle-count-item">
												<CarEntryIcon width={24} fill="var(--color-success-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.entry["4W"].auth : 0
													)}
												</Typography>
											</Box>

											<Box className="dashboard-vehicle-count-item">
												<BikeEntryIcon width={24} fill="var(--color-success-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.entry["2W"].auth : 0
													)}
												</Typography>
											</Box>
										</Box>
									</Box>

									<Box className="dashboard-vehicle-counts-content unauthorized">
										<Typography
											fontWeight={600}
											color="var(--color-warning-main)"
											sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Unauthorized
										</Typography>

										<Box className="dashboard-vehicle-counts">
											<Box className="dashboard-vehicle-count-item">
												<CarEntryIcon width={24} fill="var(--color-warning-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.entry["4W"].unauth : 0
													)}
												</Typography>
											</Box>

											<Box className="dashboard-vehicle-count-item">
												<BikeEntryIcon width={24} fill="var(--color-warning-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.entry["2W"].unauth : 0
													)}
												</Typography>
											</Box>
										</Box>

										<Box className="dashboard-vehicle-counts"></Box>
									</Box>
								</Box>
							</Box>
						)}
					</Grid>
				) : null}

				{siteConfig && siteConfig.device_type && siteConfig.device_type !== RPI_DEVICE_NAME ? (
					<Grid item xs={10} md={5}>
						{!getVehicleMetricsResponse && (getVehicleMetricsLoading || getVehicleMetricsFetching) ? (
							<Skeleton variant="rounded" animation="wave" height="153px" />
						) : (
							<Box className="dashboard-content-section dashboard-vehicle-counts-section-wrapper">
								<Typography
									variant="h6"
									color="var(--color-error-main)"
									sx={{ fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.375rem" } }}
								>
									Exit Details
								</Typography>

								<Divider sx={{ borderColor: "#00000061" }} />

								<Box className="dashboard-vehicle-counts-content-wrapper">
									<Box className="dashboard-vehicle-counts-content authorized">
										<Typography
											fontWeight={600}
											color="var(--color-success-main)"
											sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Authorized
										</Typography>

										<Box className="dashboard-vehicle-counts">
											<Box className="dashboard-vehicle-count-item">
												<CarExitIcon width={24} fill="var(--color-success-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.exit["4W"].auth : 0
													)}
												</Typography>
											</Box>

											<Box className="dashboard-vehicle-count-item">
												<BikeExitIcon width={24} fill="var(--color-success-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.exit["2W"].auth : 0
													)}
												</Typography>
											</Box>
										</Box>
									</Box>

									<Box className="dashboard-vehicle-counts-content unauthorized">
										<Typography
											fontWeight={600}
											color="var(--color-warning-main)"
											sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
										>
											Unauthorized
										</Typography>

										<Box className="dashboard-vehicle-counts">
											<Box className="dashboard-vehicle-count-item">
												<CarExitIcon width={24} fill="var(--color-warning-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.exit["4W"].unauth : 0
													)}
												</Typography>
											</Box>

											<Box className="dashboard-vehicle-count-item">
												<BikeExitIcon width={24} fill="var(--color-warning-main)" />
												<Typography
													variant="body2"
													color="#000000C2"
													sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
												>
													{Intl.NumberFormat("en-IN").format(
														getVehicleMetricsResponse ? getVehicleMetricsResponse.exit["2W"].unauth : 0
													)}
												</Typography>
											</Box>
										</Box>

										<Box className="dashboard-vehicle-counts"></Box>
									</Box>
								</Box>
							</Box>
						)}
					</Grid>
				) : null}

				{/* ROW 3 */}
				<Grid item xs={10}>
					<Box className="dashboard-content-section dashboard-events-table-section-wrapper">
						<Box className="dashboard-events-table-section-header">
							{/* Row 1: Search Input - Full Width */}
							<Box
								component="form"
								noValidate
								onSubmit={handleSubmitSearchForm}
								sx={{
									width: "100%",
									display: { xs: "block", sm: "none" }
								}}
							>
								<TextField
									variant="filled"
									placeholder="Search using number plate"
									size="small"
									value={searchInput}
									onChange={handleChangeSearchInput}
									classes={{ root: "vehicle-number-input-root" }}
									sx={{
										width: "100%",
										"& .MuiInputBase-input": {
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}
									}}
									autoComplete="off"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root dashboard-search-input" },
										startAdornment: (
											<InputAdornment position="start">
												<IconButton
													edge="start"
													size="small"
													type="submit"
													color="primary"
													sx={{ padding: { xs: "4px", sm: "6px" } }}
												>
													<SearchIcon fontSize="medium" />
												</IconButton>
											</InputAdornment>
										),
										endAdornment: eventsListFilters.search ? (
											<InputAdornment position="end">
												<IconButton
													edge="end"
													size="small"
													onClick={handleClearSearch}
													sx={{ padding: { xs: "4px", sm: "6px" } }}
												>
													<CloseIcon fontSize="inherit" />
												</IconButton>
											</InputAdornment>
										) : null
									}}
								/>
							</Box>

							{/* Row 2: All Logs Dropdown + Download Button - Mobile Only */}
							<Box
								sx={{
									display: { xs: "flex", sm: "none" },
									justifyContent: "space-between",
									alignItems: "center",
									gap: 1,
									mt: 1
								}}
							>
								<Button
									color={
										selectedLogsFilter === "authorized"
											? "success"
											: selectedLogsFilter === "blacklisted"
											? "error"
											: selectedLogsFilter === "unauthorized"
											? "warning"
											: selectedLogsFilter === "visitor"
											? "info"
											: "primary"
									}
									variant="contained"
									sx={{
										paddingX: { xs: 1, sm: 1.5, md: 2 },
										fontSize: { xs: "0.75rem", sm: "0.875rem" },
										flex: 1,
										maxWidth: "70%"
									}}
									startIcon={renderSelectedFilterIcon(selectedLogsFilter)}
									endIcon={<ArrowDropDownIcon />}
									aria-haspopup="true"
									id="logs-filter-button"
									onClick={handleOpenFilterDropdown}
									aria-expanded={logsFilterDropdownOpen ? "true" : undefined}
									aria-controls={logsFilterDropdownOpen ? "logs-filter-menu" : undefined}
									classes={{ startIcon: selectedLogsFilter ? "logs-filter-dropdown-button-icon" : "" }}
								>
									{selectedLogsFilter ? selectedLogsFilter : "All Logs"}
								</Button>

								<IconButton
									color="primary"
									onClick={handleDownloadButtonClick}
									disabled={showDownloadButtonLoader}
									sx={{ padding: { xs: "4px", sm: "6px" } }}
								>
									{showDownloadButtonLoader ? <CircularProgress size={18} /> : <DownloadIcon />}
								</IconButton>
							</Box>

							{/* Row 3: Table Settings + Add New Visitor - Mobile Only */}
							<Box
								sx={{
									display: { xs: "flex", sm: "none" },
									justifyContent: "space-between",
									alignItems: "center",
									gap: 1,
									mt: 1
								}}
							>
								<Button
									variant="outlined"
									color="primary"
									onClick={handleOpenTableSettingsDialog}
									sx={{
										fontSize: { xs: "0.75rem", sm: "0.875rem" },
										padding: { xs: "6px 12px", sm: "8px 16px" },
										flex: 1
									}}
								>
									Table Settings
								</Button>

								{siteConfig && siteConfig.mode !== CONFIG_MODE_E.MY_GATE ? (
									<LoadingButton
										variant="contained"
										color="primary"
										startIcon={<AddCircleIcon />}
										loadingPosition="start"
										onClick={() => handleOpenAddVisitorDialog(null)}
										sx={{
											fontSize: { xs: "0.75rem", sm: "0.875rem" },
											padding: { xs: "6px 12px", sm: "8px 16px" },
											flex: 1
										}}
									>
										Add New Visitor
									</LoadingButton>
								) : null}
							</Box>

							{/* Desktop Layout - Unchanged */}
							<Box
								className="dashboard-events-table-actions-wrapper"
								component="form"
								noValidate
								onSubmit={handleSubmitSearchForm}
								sx={{ display: { xs: "none", sm: "flex" } }}
							>
								<Button
									color={
										selectedLogsFilter === "authorized"
											? "success"
											: selectedLogsFilter === "blacklisted"
											? "error"
											: selectedLogsFilter === "unauthorized"
											? "warning"
											: selectedLogsFilter === "visitor"
											? "info"
											: "primary"
									}
									variant="contained"
									sx={{
										paddingX: { xs: 1, sm: 1.5, md: 2 },
										fontSize: { xs: "0.75rem", sm: "0.875rem" }
									}}
									startIcon={renderSelectedFilterIcon(selectedLogsFilter)}
									endIcon={<ArrowDropDownIcon />}
									aria-haspopup="true"
									id="logs-filter-button-desktop"
									onClick={handleOpenFilterDropdown}
									aria-expanded={logsFilterDropdownOpen ? "true" : undefined}
									aria-controls={logsFilterDropdownOpen ? "logs-filter-menu" : undefined}
									classes={{ startIcon: selectedLogsFilter ? "logs-filter-dropdown-button-icon" : "" }}
								>
									{selectedLogsFilter ? selectedLogsFilter : "All Logs"}
								</Button>

								<Menu
									id="logs-filter-menu"
									anchorEl={logsFilterDropdownAnchorElement}
									open={logsFilterDropdownOpen}
									onClose={handleCloseFilterDropdown}
									MenuListProps={{ "aria-labelledby": "logs-filter-button", disablePadding: true }}
									slotProps={{ paper: { elevation: 1 } }}
								>
									{selectedLogsFilter ? (
										<MenuItem
											onClick={() => handleChangeSelectedLogsFilter(null)}
											classes={{ root: "logs-filter-dropdown-menu-item all" }}
										>
											<ListItemIcon classes={{ root: "logs-filter-dropdown-menu-icon" }}>
												{renderSelectedFilterIcon(null)}
											</ListItemIcon>
											All Logs
											<Box className="selected-area" />
										</MenuItem>
									) : null}

									{selectedLogsFilter !== "authorized" ? (
										<MenuItem
											onClick={() => handleChangeSelectedLogsFilter("authorized")}
											classes={{ root: "logs-filter-dropdown-menu-item authorized" }}
										>
											<ListItemIcon classes={{ root: "logs-filter-dropdown-menu-icon" }}>
												{renderSelectedFilterIcon("authorized")}
											</ListItemIcon>
											Authorized
											<Box className="selected-area" />
										</MenuItem>
									) : null}

									{selectedLogsFilter !== "blacklisted" ? (
										<MenuItem
											onClick={() => handleChangeSelectedLogsFilter("blacklisted")}
											classes={{ root: "logs-filter-dropdown-menu-item blacklisted" }}
										>
											<ListItemIcon classes={{ root: "logs-filter-dropdown-menu-icon" }}>
												{renderSelectedFilterIcon("blacklisted")}
											</ListItemIcon>
											Blacklisted
											<Box className="selected-area" />
										</MenuItem>
									) : null}

									{selectedLogsFilter !== "unauthorized" ? (
										<MenuItem
											onClick={() => handleChangeSelectedLogsFilter("unauthorized")}
											classes={{ root: "logs-filter-dropdown-menu-item unauthorized" }}
										>
											<ListItemIcon classes={{ root: "logs-filter-dropdown-menu-icon" }}>
												{renderSelectedFilterIcon("unauthorized")}
											</ListItemIcon>
											Unauthorized
											<Box className="selected-area" />
										</MenuItem>
									) : null}

									{selectedLogsFilter !== "visitor" ? (
										<MenuItem
											onClick={() => handleChangeSelectedLogsFilter("visitor")}
											classes={{ root: "logs-filter-dropdown-menu-item visitor" }}
										>
											<ListItemIcon classes={{ root: "logs-filter-dropdown-menu-icon" }}>
												{renderSelectedFilterIcon("visitor")}
											</ListItemIcon>
											Visitor
											<Box className="selected-area" />
										</MenuItem>
									) : null}
								</Menu>

								<TextField
									variant="filled"
									placeholder="Search using number plate"
									size="small"
									value={searchInput}
									onChange={handleChangeSearchInput}
									classes={{ root: "vehicle-number-input-root" }}
									sx={{
										width: { xs: "100%", sm: "280px", md: "300px" },
										"& .MuiInputBase-input": {
											fontSize: { xs: "0.875rem", sm: "1rem" }
										}
									}}
									autoComplete="off"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root dashboard-search-input" },
										startAdornment: (
											<InputAdornment position="start">
												<IconButton
													edge="start"
													size="small"
													type="submit"
													color="primary"
													sx={{ padding: { xs: "4px", sm: "6px" } }}
												>
													<SearchIcon fontSize="medium" />
												</IconButton>
											</InputAdornment>
										),
										endAdornment: eventsListFilters.search ? (
											<InputAdornment position="end">
												<IconButton
													edge="end"
													size="small"
													onClick={handleClearSearch}
													sx={{ padding: { xs: "4px", sm: "6px" } }}
												>
													<CloseIcon fontSize="inherit" />
												</IconButton>
											</InputAdornment>
										) : null
									}}
								/>
							</Box>

							<Box className="dashboard-events-table-actions-wrapper" sx={{ display: { xs: "none", sm: "flex" } }}>
								<Button
									variant="outlined"
									color="primary"
									onClick={handleOpenTableSettingsDialog}
									sx={{
										fontSize: { xs: "0.75rem", sm: "0.875rem" },
										padding: { xs: "6px 12px", sm: "8px 16px" }
									}}
								>
									Table Settings
								</Button>

								{siteConfig && siteConfig.mode !== CONFIG_MODE_E.MY_GATE ? (
									<LoadingButton
										variant="contained"
										color="primary"
										startIcon={<AddCircleIcon />}
										loadingPosition="start"
										onClick={() => handleOpenAddVisitorDialog(null)}
										sx={{
											fontSize: { xs: "0.75rem", sm: "0.875rem" },
											padding: { xs: "6px 12px", sm: "8px 16px" }
										}}
									>
										Add New Visitor
									</LoadingButton>
								) : null}

								<IconButton
									color="primary"
									onClick={handleDownloadButtonClick}
									disabled={showDownloadButtonLoader}
									sx={{ padding: { xs: "4px", sm: "6px" } }}
								>
									{showDownloadButtonLoader ? <CircularProgress size={18} /> : <DownloadIcon />}
								</IconButton>
							</Box>
						</Box>

						{getUserSettingsLoading || getUserSettingsFetching ? (
							<Skeleton variant="rounded" animation="wave" height="400px" />
						) : (
							<DataGrid
								sx={{ height: 0, flexGrow: 1 }}
								columns={getColumnsData()}
								rows={rowsData}
								disableRowSelectionOnClick
								disableColumnMenu
								rowSpacingType="border"
								density="compact"
								columnHeaderHeight={65}
								pageSizeOptions={[ROWS_PER_PAGE]}
								slots={{ pagination: DashboardPagination }}
								slotProps={{
									footer: { sx: { justifyContent: "center" } },
									pagination: { classes: { toolbar: "vehicle-logs-table-pagination-toolbar" } }
								}}
								paginationMode="server"
								paginationModel={{
									page: eventsListFilters.page ? eventsListFilters.page - 1 : 0,
									pageSize: ROWS_PER_PAGE
								}}
								hideFooter={(getEventsListResponse?.count ?? 0) <= ROWS_PER_PAGE}
								onPaginationModelChange={handleVehicleLogsPageChange}
								rowCount={getEventsListResponse?.count ?? 0}
								loading={
									(!getEventsListResponse || getEventsListResponse.results.length <= 0 || isFilterApplied) &&
									(getEventsListLoading || getEventsListFetching)
								}
								classes={{
									columnSeparator: "vehicle-logs-table-column-separator",
									root: "vehicle-logs-table-wrapper",
									virtualScroller: "vehicle-logs-table-virtual-scroller",
									iconSeparator: "vehicle-logs-table-icon-separator"
								}}
								columnVisibilityModel={ColumnVisibilityModel}
							/>
						)}
					</Box>
				</Grid>
			</Grid>

			{notificationsState.showNotifications && displayNotifications.length > 0 ? (
				<Box className="notification-wrapper">
					<Box className={`notification-type-indicator ${displayNotifications[0].data.auth_type}`} />

					<Box
						className={`notification-vehicle-type-wrapper ${displayNotifications[0].data.is_entry ? "info" : "error"}`}
					>
						{displayNotifications[0].data.is_entry ? (
							displayNotifications[0].data.vehicle_type === 2 ? (
								<BikeEntryIcon width={32} fill="#01579B" />
							) : (
								<CarEntryIcon width={32} fill="#01579B" />
							)
						) : displayNotifications[0].data.vehicle_type === 2 ? (
							<BikeExitIcon width={32} fill="var(--color-error-main)" />
						) : (
							<CarExitIcon width={32} fill="var(--color-error-main)" />
						)}
					</Box>

					<Box className="notification-details-wrapper">
						<Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
							{displayNotifications[0].data.number_plate}
						</Typography>

						<Box className="notification-timestamp-wrapper">
							<WatchLaterIcon fontSize="small" />

							<Typography variant="body2" color="#00000099" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
								{moment(displayNotifications[0].data.timestamp, EVENT_LOGS_REQUEST_TIME_FORMAT).format("hh:mm:ss A")}
							</Typography>
						</Box>
					</Box>

					<Box className="notification-number-plate-image-wrapper">
						<Box
							sx={{
								width: "90%",
								height: "90%",
								// backgroundImage: `url(${SamplePlateImage})`,
								backgroundImage: `url(data:image/png;base64,${displayNotifications[0].data.number_plate_image})`,
								backgroundRepeat: "no-repeat",
								backgroundSize: "contain",
								backgroundPosition: "center"
							}}
						/>
					</Box>

					<Box
						className="notification-close-icon-wrapper"
						onClick={() => handleRemoveCurrentNotification(displayNotifications[0].messageId)}
					>
						<CloseIcon sx={{ fontSize: "16px" }} />
					</Box>
				</Box>
			) : null}

			<AddVisitorDialog
				open={showAddVisitorDialog}
				selectedEvent={addVisitorSelectedEvent}
				onClose={handleCloseAddVisitorDialog}
			/>

			<LogDetailsDialog
				open={showLogDetailsDialog}
				eventLogDetails={selectedLogDetails}
				previousButtonDisabled={detailsPreviousButtonDisabled}
				nextButtonDisabled={detailsNextButtonDisabled}
				onClose={handleCloseLogDetailsDialog}
				onNext={handleChangeToNextAlert}
				onPrevious={handleChangeToPreviousAlert}
				pageChangeLoading={!!alertPageChanging.current && (getEventsListLoading || getEventsListFetching)}
			/>

			<TableSettingsDialog
				open={showTableSettingsDialog}
				columns={getColumnsData()}
				columnVisibility={columnVisibility}
				onClose={handleCloseTableSettingsDialog}
				setColumnVisibility={setColumnVisibility}
			/>
		</Box>
	);
};

export default Dashboard;
